import type { ExplorationFilter, ExplorationVolume } from '../../../types/exploration';
import type { SelectOption } from '~/shared/utils/types';

import { isCapacityRangeNarrowed, type CapacityBounds } from './capacityFormat';

export type { CapacityBounds } from './capacityFormat';

export interface ExplorationFilterPanelState {
  activationStatuses: string[];
  supportStatuses: string[];
  accountFilters: SelectOption[];
  clusterFilters: SelectOption[];
  infrastructureFilters: string[];
  regionFilters: string[];
  nodeGroupFilters: string[];
  diskTypeFilters: string[];
  instanceFilters: SelectOption[];
  volumeIdFilters: SelectOption[];
  tagFilters: SelectOption[];
  capacityMin: number;
  capacityMax: number;
}

export function getCapacityBounds(volumes: ExplorationVolume[]): CapacityBounds {
  if (!volumes.length) return { min: 0, max: 1 };
  const maxTib = Math.ceil(Math.max(...volumes.map((v) => (v.size || 0) / 1024)));
  return { min: 0, max: Math.max(maxTib, 1) };
}

export function emptyPanelState(bounds: CapacityBounds): ExplorationFilterPanelState {
  return {
    activationStatuses: [],
    supportStatuses: [],
    accountFilters: [],
    clusterFilters: [],
    infrastructureFilters: [],
    regionFilters: [],
    nodeGroupFilters: [],
    diskTypeFilters: [],
    instanceFilters: [],
    volumeIdFilters: [],
    tagFilters: [],
    capacityMin: bounds.min,
    capacityMax: bounds.max,
  };
}

function tagOptionToFilter(option: SelectOption): ExplorationFilter {
  const tagKey = option.group.startsWith('tag:') ? option.group.slice(4) : '';
  const colonIdx = option.id.indexOf(':');
  const value = colonIdx >= 0 ? option.id.slice(colonIdx + 1) : option.id;
  return { type: 'tag', key: tagKey, value };
}

function tagFilterToOption(filter: Extract<ExplorationFilter, { type: 'tag' }>): SelectOption {
  return {
    id: `${filter.key}:${filter.value}`,
    name: `${filter.key}:${filter.value}`,
    group: `tag:${filter.key}` as SelectOption['group'],
  };
}

export function explorationFiltersToPanelState(
  filters: ExplorationFilter[],
  bounds: CapacityBounds
): ExplorationFilterPanelState {
  const state = emptyPanelState(bounds);

  filters.forEach((f) => {
    switch (f.type) {
      case 'activationStatus':
        state.activationStatuses.push(f.value);
        break;
      case 'supportStatus':
        state.supportStatuses.push(f.value);
        break;
      case 'accountId':
        state.accountFilters.push({ id: f.value, name: f.value, group: 'accountId' });
        break;
      case 'cluster':
        state.clusterFilters.push({ id: f.value, name: f.value, group: 'clusterName' });
        break;
      case 'infrastructure':
        state.infrastructureFilters.push(f.value);
        break;
      case 'region':
        state.regionFilters.push(f.value);
        break;
      case 'nodeGroup':
        state.nodeGroupFilters.push(f.value);
        break;
      case 'diskType':
        state.diskTypeFilters.push(f.value);
        break;
      case 'instanceId':
        state.instanceFilters.push({ id: f.value, name: f.value, group: 'instanceId' });
        break;
      case 'volumeId':
        state.volumeIdFilters.push({ id: f.value, name: f.value, group: 'volumeId' });
        break;
      case 'tag':
        state.tagFilters.push(tagFilterToOption(f));
        break;
      case 'capacity':
        state.capacityMin = f.min;
        state.capacityMax = f.max;
        break;
    }
  });

  return state;
}

export function panelStateToExplorationFilters(
  state: ExplorationFilterPanelState,
  bounds: CapacityBounds
): ExplorationFilter[] {
  const filters: ExplorationFilter[] = [];

  state.activationStatuses.forEach((value) => {
    filters.push({ type: 'activationStatus', value });
  });
  state.supportStatuses.forEach((value) => {
    filters.push({ type: 'supportStatus', value });
  });
  state.accountFilters.forEach((option) => {
    filters.push({ type: 'accountId', value: option.id });
  });
  state.clusterFilters.forEach((option) => {
    filters.push({ type: 'cluster', value: option.id });
  });
  state.infrastructureFilters.forEach((value) => {
    filters.push({ type: 'infrastructure', value });
  });
  state.regionFilters.forEach((value) => {
    filters.push({ type: 'region', value });
  });
  state.nodeGroupFilters.forEach((value) => {
    filters.push({ type: 'nodeGroup', value });
  });
  state.diskTypeFilters.forEach((value) => {
    filters.push({ type: 'diskType', value });
  });
  state.instanceFilters.forEach((option) => {
    filters.push({ type: 'instanceId', value: option.id });
  });
  state.volumeIdFilters.forEach((option) => {
    filters.push({ type: 'volumeId', value: option.id });
  });
  state.tagFilters.forEach((option) => {
    filters.push(tagOptionToFilter(option));
  });

  if (isCapacityRangeNarrowed(state.capacityMin, state.capacityMax, bounds)) {
    filters.push({ type: 'capacity', min: state.capacityMin, max: state.capacityMax });
  }

  return filters;
}

export function hasPendingFilters(
  state: ExplorationFilterPanelState,
  bounds: CapacityBounds
): boolean {
  return (
    state.activationStatuses.length > 0 ||
    state.supportStatuses.length > 0 ||
    state.accountFilters.length > 0 ||
    state.clusterFilters.length > 0 ||
    state.infrastructureFilters.length > 0 ||
    state.regionFilters.length > 0 ||
    state.nodeGroupFilters.length > 0 ||
    state.diskTypeFilters.length > 0 ||
    state.instanceFilters.length > 0 ||
    state.volumeIdFilters.length > 0 ||
    state.tagFilters.length > 0 ||
    isCapacityRangeNarrowed(state.capacityMin, state.capacityMax, bounds)
  );
}
